/** Ball / not-ball grid (port of ball_grid.py's inline page, same
 *  address /labeling/ball-grid/).
 *
 * Click cycles unmarked → ball → not ball → unsure → unmarked. Two
 * submits, exactly the Jinja pair: "submit all" labels the unmarked
 *  as Not ball; "submit" sends only marks and releases the rest back
 * to the queue. The Jinja page fetched relative URLs; the port calls
 * the same endpoints absolutely — the APIs stayed on Flask.
 */
import { useCallback, useEffect, useState } from 'react'

import { api } from '../../lib/api'
import './labeling.css'

type Task = { id: number; image: string; game?: string }
type Mark = 'ball' | 'notball' | 'unsure'
const ORDER: (Mark | undefined)[] = [undefined, 'ball', 'notball', 'unsure']
const NAME: Record<Mark, string> = { ball: 'Ball', notball: 'Not ball', unsure: 'Unsure' }

export default function BallGrid() {
  const [tasks, setTasks] = useState<Task[]>([])
  const [state, setState] = useState<Record<number, Mark>>({})
  const [status, setStatus] = useState('loading...')

  const loadBatch = useCallback(() => {
    setStatus('loading...')
    api<{ tasks?: Task[]; error?: string }>('/labeling/ball-grid/batch')
      .then((j) => {
        if (j.error) { setStatus(j.error); return }
        setTasks(j.tasks ?? [])
        setState({})
        setStatus('')
      })
      .catch((e: Error) => setStatus(e.message))
  }, [])

  useEffect(() => { loadBatch() }, [loadBatch])

  const cycle = (id: number) => {
    setState((prev) => {
      const next = ORDER[(ORDER.indexOf(prev[id]) + 1) % 4]
      const out = { ...prev }
      if (next === undefined) delete out[id]
      else out[id] = next
      return out
    })
  }

  const invert = () => {
    setState((prev) => {
      const out = { ...prev }
      for (const t of tasks) if (!out[t.id]) out[t.id] = 'ball'
      return out
    })
  }

  const send = async (labels: { task_id: number; choice: string }[], release: number[]) => {
    setStatus('submitting...')
    try {
      const j = await api<{ error?: string }>('/labeling/ball-grid/submit', {
        method: 'POST', body: JSON.stringify({ labels, release }),
      })
      if (j?.error) { setStatus(j.error); return }
      loadBatch()
    } catch (e) {
      setStatus(e instanceof Error ? e.message : 'submit failed')
    }
  }

  const submitBatch = () =>
    send(tasks.map((t) => ({ task_id: t.id, choice: NAME[state[t.id]!] || 'Not ball' })), [])
  const submitMarked = () =>
    send(tasks.filter((t) => state[t.id]).map((t) => ({ task_id: t.id, choice: NAME[state[t.id]!] })),
         tasks.filter((t) => !state[t.id]).map((t) => t.id))

  const v = Object.values(state)
  const b = v.filter((x) => x === 'ball').length
  const nb = v.filter((x) => x === 'notball').length
  const u = v.filter((x) => x === 'unsure').length
  const stats = status ||
    `${tasks.length} shown · ${b} ball · ${nb} not ball · ${u} unsure · ${tasks.length - b - nb - u} unmarked`

  return (
    <div className="lab-ball">
      <div id="bar">
        {' '}<b>ball grid</b>{' '}
        <span className="k">click cycles: ball → not ball → unsure → clear</span>{' '}
        <button className="sec" onClick={invert}>invert (all→ball)</button>{' '}
        <button onClick={submitMarked}>submit</button>{' '}
        <button className="sec" onClick={submitBatch}>submit all (unmarked = not ball)</button>{' '}
        <span id="stats">{stats}</span>
      </div>
      <div id="grid">
        {tasks.map((t) => (
          <div key={t.id} className={`cell ${state[t.id] || ''}`}
               onClick={() => cycle(t.id)}>
            <img loading="lazy" src={t.image} alt="" />
            <span className="tag">{t.game || ''}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
